import type { HubConnection } from '@microsoft/signalr'
import { useEffect, useContext } from 'react'

import { BadRequestError } from '@/lib/apiError'
import connectToSignalR from '@/lib/signalR/signalRConnect'
import { newEvent, newMessage, newUserGroupCallbacks } from '@/lib/signalR/store/actions'
import { SignalRDispatchContext } from '@/lib/signalR/store/context'
import { createSignalRApiService } from '@/lib/signalRApiService'

interface UseSignalRArgs {
  customerId?: string
  userId?: string
}

export const useSignalR = ({ customerId, userId }: UseSignalRArgs) => {
  const { dispatch } = useContext(SignalRDispatchContext)

  useEffect(() => {
    if (!userId || !customerId || !dispatch) {
      return
    }

    let connection: HubConnection | undefined
    ;(async () => {
      console.debug('Clearing existing SignalR configuration')

      dispatch(
        newUserGroupCallbacks({
          addUserToGroup: () => {
            console.debug('Adding user to SignalR group')

            console.warn(
              'Failed to add user to SignalR group because SignalR is not connected',
            )
          },
          removeUserFromGroup: () => {
            console.debug('Removing user from SignalR group')

            console.warn(
              'Failed to remove user from SignalR group because SignalR is not connected',
            )
          },
        }),
      )

      console.debug('Requesting SignalR connection info')

      const signalRApiService = createSignalRApiService({ userId, customerId })

      const response = await (async () => {
        try {
          return signalRApiService.negotiate({})
        } catch (e) {
          return
        }
      })()

      if (!response) {
        console.error('Failed to retrieve SignalR connection info')

        return
      }

      console.debug('Received SignalR connection info')

      try {
        connection = await connectToSignalR({
          accessToken: response.accessToken,
          url: response.url,
          onNewMessageReceived: data => dispatch(newMessage(data)),
          onNewEventReceived: data => dispatch(newEvent(data)),
        })
      } catch (error) {
        console.error('Failed to connect to SignalR.', error)
      }

      if (!connection) {
        console.error('Did not retrieve a connection after connecting to SignalR.')
        return
      }

      const signalRConnectionId = connection.connectionId

      if (signalRConnectionId === null) {
        console.error('Did not retrieve a connection ID after connecting to SignalR.')
        return
      }

      dispatch(
        newUserGroupCallbacks({
          addUserToGroup: async ({ groupName, email }) => {
            console.debug('Adding user to SignalR group')

            try {
              await signalRApiService.addUserToGroup({
                customerId,
                signalRConnectionId,
                userId,
                groupName,
                email,
              })
            } catch (e) {
              if (e instanceof BadRequestError) {
                console.warn('Received bad request error', {
                  connectionId: signalRConnectionId,
                  personId: userId,
                  groupName,
                  email,
                })

                return
              }

              console.warn('Failed to add user to SignalR group')
            }
          },
          removeUserFromGroup: async ({ groupName }) => {
            console.debug('Removing user from SignalR group')

            try {
              await signalRApiService.removeUserFromGroup({
                signalRConnectionId,
                userId,
                groupName,
              })
            } catch (e) {
              if (e instanceof BadRequestError) {
                console.warn('Received bad request error', {
                  connectionId: signalRConnectionId,
                  personId: userId,
                  groupName,
                })

                return
              }

              console.warn('Failed to remove user from SignalR group')
            }
          },
        }),
      )
    })()

    return () => {
      console.debug('Disconnecting from SignalR')
      connection?.stop()
    }
  }, [customerId, userId, dispatch])
}
